import React, { useEffect, useState, useCallback, useRef, Suspense } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { isNative } from './utils/platform';
import { setNavigator, clearNavigator } from './services/navigation';
import { initNative } from './native/initNative';
import { pushOverlay } from './native/backStack';
import PwaPrompts from './components/PwaPrompts';
import { I18nextProvider, useTranslation } from 'react-i18next';
import useAuthStore from './store/authStore';
import useSidebarStore from './store/sidebarStore';
import { useViewport } from './hooks/useViewport';
import { YearProvider } from './context/YearContext';
import { ToastProvider, useToastContext } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import i18n from './i18n/config';
import './i18n/centerTermsSync';
import { applyDocumentDirection } from './i18n/direction';
import ToastContainer from './components/ToastContainer';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import MobileHeader from './components/MobileHeader';
import SideDrawer from './components/SideDrawer';
import NotificationPanel from './components/NotificationPanel';
import { PageErrorBoundary } from './components/ErrorBoundary';
import api from './services/api';
import { Loader } from 'lucide-react';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StudentsPage from './pages/StudentsPage';
import TutorialOverlay from './components/TutorialOverlay';
import OfflineIndicator from './components/OfflineIndicator';
import CommandPalette from './components/CommandPalette';
import PageTransition from './components/PageTransition';
import FabRadial from './components/FabRadial';
import PlanGate from './components/PlanGate';

// Lazy-loaded pages (code splitting for bundle size reduction).
//
// Routes marked with `webpackPrefetch: true` emit a `<link rel="prefetch">`
// in the rendered HTML so the browser idle-fetches the chunk after first
// paint — when the user navigates there, the JS is already in disk cache.
// Browsers automatically skip prefetch on slow networks / data-saver mode,
// so cellular teachers aren't hit with unwanted bandwidth.
//
// Prefetch is scoped to the highest-volume follow-up routes from /dashboard
// or /teacher/dashboard: lessons, attendance, calendar, lesson-attendance,
// and the student detail page (visited from the Students table). All other
// routes load on-demand without a prefetch hint.
const TeachersPage = React.lazy(() => import('./pages/TeachersPage'));
const GroupsPage = React.lazy(() => import('./pages/GroupsPage'));
const GroupDetailsPage = React.lazy(() => import('./pages/GroupDetailsPage'));
const GroupPerformanceReportPage = React.lazy(() => import('./pages/GroupPerformanceReportPage'));
const JibayaPage = React.lazy(() => import('./pages/JibayaPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const TeacherDashboardPage = React.lazy(() => import('./pages/TeacherDashboardPage'));
const TeacherChecklistsPage = React.lazy(() => import('./pages/TeacherChecklistsPage'));
const TeacherAttendancePage = React.lazy(() => import('./pages/TeacherAttendancePage'));
// StudentEditPage removed in Phase 6 — replaced by LearningStudentPage /
// TherapyClientPage / HairClientPage under StudentDetailRouter.
const TeacherEditPage = React.lazy(() => import('./pages/TeacherEditPage'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const AnalyticsDashboard = React.lazy(() => import('./pages/AnalyticsDashboard'));
const SurveysPage = React.lazy(() => import('./pages/SurveysPage'));
const SurveyResponsePage = React.lazy(() => import('./pages/SurveyResponsePage'));
const CalendarPage = React.lazy(() => import(/* webpackPrefetch: true */ './pages/CalendarPage'));
// Weekly timetable (static day × period wall chart) — separate from the
// date-based calendar above; reads `timetableSlots`, never `lessons`.
const TimetablePage = React.lazy(() => import('./pages/TimetablePage'));
const LessonsPage = React.lazy(() => import(/* webpackPrefetch: true */ './pages/LessonsPage'));
const LessonAttendancePage = React.lazy(() => import(/* webpackPrefetch: true */ './pages/LessonAttendancePage'));
// StudentDetailPage removed in Phase 6 — merged into the three per-center pages.
const TherapyAnalyticsPage = React.lazy(() => import('./pages/TherapyAnalyticsPage'));
const AuditLogPage = React.lazy(() => import('./pages/AuditLogPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const ConnectClaudePage = React.lazy(() => import('./pages/ConnectClaudePage'));
const PublicSurveyPage = React.lazy(() => import('./pages/PublicSurveyPage'));
const PublicLeadsSharePage = React.lazy(() => import('./pages/PublicLeadsSharePage'));
const PublicLessonPage = React.lazy(() => import('./pages/PublicLessonPage'));
const PublicGroupRosterPage = React.lazy(() => import('./pages/PublicGroupRosterPage'));
const PublicTimetablePage = React.lazy(() => import('./pages/PublicTimetablePage'));
const PublicMeetingPage = React.lazy(() => import('./pages/PublicMeetingPage'));
const PublicOnboardingPage = React.lazy(() => import('./pages/PublicOnboardingPage'));
// Sport sub-type: public signed client-intake form (WhatsApp link, token-authorized).
const PublicIntakePage = React.lazy(() => import('./pages/PublicIntakePage'));
// Staff contract e-signature (WhatsApp link, token-authorized, single use).
const PublicSignContractPage = React.lazy(() => import('./pages/PublicSignContractPage'));
const PublicSportConfirmPage = React.lazy(() => import('./pages/PublicSportConfirmPage'));
const PublicSportWeekConfirmPage = React.lazy(() => import('./pages/PublicSportWeekConfirmPage'));
const SportPayrollPage = React.lazy(() => import('./pages/SportPayrollPage'));
const TeacherHoursPage = React.lazy(() => import('./pages/TeacherHoursPage'));
const TeacherPayoutsPage = React.lazy(() => import('./pages/TeacherPayoutsPage'));
const SportBalancesPage = React.lazy(() => import('./pages/SportBalancesPage'));
const OnboardingSubmissionsPage = React.lazy(() => import('./pages/OnboardingSubmissionsPage'));
// Course Registration module (learning centers)
const RegistrationCoursesPage = React.lazy(() => import('./pages/RegistrationCoursesPage'));
const RegistrationCourseDetailPage = React.lazy(() => import('./pages/RegistrationCourseDetailPage'));
const RegistrationGroupDetailPage = React.lazy(() => import('./pages/RegistrationGroupDetailPage'));
const RegistrationGroupAnalyticsPage = React.lazy(() => import('./pages/RegistrationGroupAnalyticsPage'));
const PublicRegisterPage = React.lazy(() => import('./pages/PublicRegisterPage'));
const PublicRegisterGroupPage = React.lazy(() => import('./pages/PublicRegisterGroupPage'));
// Caffe sub-type — tournament management (gated to learningSubType === 'caffe').
const TournamentsPage = React.lazy(() => import('./pages/TournamentsPage'));
const TournamentDetailPage = React.lazy(() => import('./pages/TournamentDetailPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
// Public marketing website (no auth, no AppLayout) — served at /website.
const WebsiteHomePage = React.lazy(() => import('./pages/website/WebsiteHomePage'));
const WebsitePricingPage = React.lazy(() => import('./pages/website/WebsitePricingPage'));
const WebsiteContactPage = React.lazy(() => import('./pages/website/WebsiteContactPage'));
const WebsiteClaudePage = React.lazy(() => import('./pages/website/WebsiteClaudePage'));
const WebsitePrivacyPage = React.lazy(() => import('./pages/website/WebsitePrivacyPage'));
const WebsiteSecurityPage = React.lazy(() => import('./pages/website/WebsiteSecurityPage'));
const WebsiteTermsPage = React.lazy(() => import('./pages/website/WebsiteTermsPage'));
const TransplantCrmPage = React.lazy(() => import('./pages/TransplantCrmPage'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
const TransplantQuickImagesPage = React.lazy(() => import('./pages/TransplantQuickImagesPage'));
// FollowupPlansPage / PrpProspectsPage / PostOpTrackingPage retired —
// their routes redirect to /patients with a lifecycle filter applied.
// Files remain on disk for reference (and in case a deep-link needs
// the source-of-truth UI) but are no longer wired into the router.
const WorkersPage = React.lazy(() => import('./pages/WorkersPage'));
const ClinicDashboardPage = React.lazy(() => import('./pages/ClinicDashboardPage'));
const WorkerDashboardPage = React.lazy(() => import('./pages/WorkerDashboardPage'));
const AutomationPage = React.lazy(() => import('./pages/AutomationPage'));
const MetaLeadsConnectPage = React.lazy(() => import('./pages/automation/MetaLeadsConnect'));
const MetaLeadsStatusPage = React.lazy(() => import('./pages/automation/MetaLeadsStatus'));
const MetaLeadsListPage = React.lazy(() => import('./pages/automation/MetaLeadsList'));
const AllCentersLeadsPage = React.lazy(() => import('./pages/automation/AllCentersLeads'));
const InventoryPage = React.lazy(() => import('./pages/InventoryPage'));
const TreatmentsPage = React.lazy(() => import('./pages/TreatmentsPage'));
const FollowUpsPage = React.lazy(() => import('./pages/FollowUpsPage'));
const ClinicDocumentsPage = React.lazy(() => import('./pages/ClinicDocumentsPage'));
const StudentDetailRouter = React.lazy(() => import(/* webpackPrefetch: true */ './pages/StudentDetailRouter'));
const WhatsAppTemplatesPage = React.lazy(() => import('./pages/WhatsAppTemplatesPage'));
const WhatsAppBulkSendPage = React.lazy(() => import('./pages/WhatsAppBulkSendPage'));
const WhatsAppInboxPage = React.lazy(() => import('./pages/WhatsAppInboxPage'));
const WhatsAppBulkRunDetailPage = React.lazy(() => import('./pages/WhatsAppBulkRunDetailPage'));
const BirthdayGiftsPage = React.lazy(() => import('./pages/BirthdayGiftsPage'));
const BirthdaysPage = React.lazy(() => import('./pages/BirthdaysPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const TeacherSurveysPage = React.lazy(() => import('./pages/TeacherSurveysPage'));
// Worker Attendance — admin Live/Timesheets/Reports/Settings (plan §12, §13.2).
// Mounted at /staff-attendance: the legacy /attendance path is already a
// learning-center lesson-attendance redirect, so the new admin page (gated to
// therapy + hair_transplant) gets its own path to avoid clobbering it.
const AttendancePage = React.lazy(() => import('./pages/AttendancePage'));

function PageLoader() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Loader size={36} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary-dark)' }} />
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/**
 * LegacyRedirect — sends old `/student/:studentId` and
 * `/student/:studentId/details` URLs to the new canonical detail route.
 *
 * The new model uses two URL aliases for the same `StudentDetailRouter`:
 *   - `/students/:studentId` for learning centers
 *   - `/clients/:studentId`  for therapy + hair-transplant centers
 * so the address bar reads semantically correctly per center type.
 *
 * If the user is unauthenticated we kick them to /login first; otherwise we
 * redirect to whichever alias matches their current center type.
 */
function LegacyRedirect() {
  const { studentId } = useParams();
  const { token, centerType } = useAuthStore();

  if (!token) {
    return <Navigate to="/login" replace />;
  }
  const base = centerType === 'learning' ? '/students' : '/clients';
  return <Navigate to={`${base}/${studentId}`} replace />;
}

function AttendanceLessonRedirect() {
  const { lessonId } = useParams();
  return <Navigate to={`/lessons/${lessonId}/attendance`} replace />;
}

/**
 * RedirectWithSearch — used for the hair-transplant consolidation:
 * preserves the incoming query string when redirecting and lets the
 * caller layer additional params (e.g. {lifecycle: 'prp_eligible'} when
 * redirecting from /prp-prospects). If a key is already present on the
 * incoming URL, the existing value wins — extraParams only fills in
 * when the source URL didn't specify.
 */
function RedirectWithSearch({ to, extraParams }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (extraParams) {
    Object.entries(extraParams).forEach(([k, v]) => {
      if (!params.has(k)) params.set(k, v);
    });
  }
  const search = params.toString();
  return <Navigate to={search ? `${to}?${search}` : to} replace />;
}

function ProtectedRoute({
  children,
  allowedInTherapyCenter = true,
  allowedCenterTypes,
  requiresAdmin = false,
  requiresSuperAdmin = false,
}) {
  const { token, centerType, user } = useAuthStore();
  const location = useLocation();

  if (!token) {
    // Come back here after signing in (e.g. a CenterHub link from ChatGPT).
    // LoginPage.safeNextPath only honours known in-app paths.
    const here = `${location.pathname}${location.search}`;
    return <Navigate to={here && here !== '/' ? `/login?next=${encodeURIComponent(here)}` : '/login'} replace />;
  }

  // Cross-tenant screens. This is a UX guard only — the backend re-checks the
  // role on every request, and is the actual security boundary.
  if (requiresSuperAdmin && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Workers (role=teacher) can only access worker-allowed routes
  if (requiresAdmin && user?.role === 'teacher') {
    const workerHome = centerType === 'hair_transplant' ? '/worker/dashboard' : '/teacher/dashboard';
    return <Navigate to={workerHome} replace />;
  }

  // If specific center types are allowed, check against them
  if (allowedCenterTypes && !allowedCenterTypes.includes(centerType)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Clinic centers (therapy, hair transplant, pharma clinic) never see the
  // learning-only pages (groups, teachers, lessons, jibaya, analytics).
  if ((centerType === 'therapy' || centerType === 'hair_transplant' || centerType === 'pharma_clinic') && !allowedInTherapyCenter) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// pharma_clinic centers have no dashboard — their home is the Orders screen.
function DashboardRouter() {
  const centerType = useAuthStore((s) => s.centerType);
  if (centerType === 'pharma_clinic') return <Navigate to="/orders" replace />;
  return <DashboardPage />;
}

function AppLayout({ children }) {
  const { i18n, t } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const { user, token, centerType } = useAuthStore();
  // The page error boundary starts fresh on every page: otherwise one page's
  // crash stays on screen for every page the user opens next, until reload.
  const { pathname } = useLocation();
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);
  const { isMobile: isPhone, isTablet, isMobileOrTablet } = useViewport();
  // Phase 1.2: "mobile" treatment (mobile header + bottom nav) extends to
  // tablet so iPad gets the same fast-access UX. Sidebar is rendered in
  // collapsed icon-rail form on tablet, hidden on phone.
  const isMobile = isMobileOrTablet;

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
  const [isNotifLoading, setIsNotifLoading] = useState(false);
  const notifPanelRef = useRef(null);

  // Set center type data attribute for CSS theming
  useEffect(() => {
    if (centerType) {
      document.documentElement.setAttribute('data-center-type', centerType);
    }
  }, [centerType]);

  // Re-sync which centers this account belongs to, once per app load.
  // Memberships are otherwise only written at login/switch, so being added to
  // (or removed from) a center stayed invisible until the next sign-in.
  useEffect(() => {
    if (!token) return;
    useAuthStore.getState().refreshMemberships();
  }, [token]);

  // Native hardware-back: register open overlays so back dismisses them before
  // navigating/exiting. No-op on web (isNative === false).
  useEffect(() => {
    if (!isNative || !isSideDrawerOpen) return undefined;
    return pushOverlay(() => setIsSideDrawerOpen(false));
  }, [isSideDrawerOpen]);
  useEffect(() => {
    if (!isNative || !isNotifPanelOpen) return undefined;
    return pushOverlay(() => setIsNotifPanelOpen(false));
  }, [isNotifPanelOpen]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get('/notifications');
      const list = Array.isArray(response) ? response : (response.data || []);
      setNotifications(list);
      setUnreadCount(list.filter(n => !n.isRead).length);
    } catch (err) {
      // Silently fail — notifications are non-critical
    }
  }, [token]);

  // Fetch unread count (lightweight polling endpoint)
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get('/notifications/unread-count');
      const count = response.count ?? response.data?.count ?? 0;
      setUnreadCount(count);
    } catch (err) {
      // Silently fail
    }
  }, [token]);

  // Initial fetch + 60-second polling
  useEffect(() => {
    if (!token) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [token, fetchUnreadCount]);

  // Handle bell click — open panel and fetch full notifications
  const handleNotificationClick = useCallback(() => {
    setIsNotifPanelOpen(prev => !prev);
    if (!isNotifPanelOpen) {
      setIsNotifLoading(true);
      fetchNotifications().finally(() => setIsNotifLoading(false));
    }
  }, [isNotifPanelOpen, fetchNotifications]);

  // Mark single notification as read
  const handleMarkRead = useCallback(async (notifId) => {
    try {
      await api.put(`/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Silently fail
    }
  }, []);

  // Mark all notifications as read
  const handleMarkAllRead = useCallback(async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      // Silently fail
    }
  }, []);

  // Hide sidebar for teacher users (except workers in hair_transplant centers)
  const shouldShowSidebar = user?.role !== 'teacher' || centerType === 'hair_transplant';
  const { collapsed: sidebarStoreCollapsed } = useSidebarStore();
  // Tablet always forces collapsed (icon-rail). Desktop respects user preference.
  const sidebarCollapsed = isTablet ? true : sidebarStoreCollapsed;
  const sidebarWidth = sidebarCollapsed ? '4.5rem' : '16rem';

  const handleSearchClick = () => {
    // TODO: Implement search functionality
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: 'var(--color-surface-alt)', minHeight: '100vh', direction: isRTL ? 'rtl' : 'ltr' }}>
      <a href="#main-content" className="skip-link">{t('common.skipToContent')}</a>

      {/* Mobile Header (App Bar) - Only on Mobile */}
      {isMobile && (
        <MobileHeader
          onSearchClick={handleSearchClick}
          onNotificationClick={handleNotificationClick}
          unreadNotifications={unreadCount}
        />
      )}

      {/* Desktop Header - Only on Desktop */}
      {!isMobile && (
        <Header
          onNotificationClick={handleNotificationClick}
          unreadCount={unreadCount}
        />
      )}

      {/* Notification Panel (positioned relative to header) */}
      <NotificationPanel
        isOpen={isNotifPanelOpen}
        onClose={() => setIsNotifPanelOpen(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        isLoading={isNotifLoading}
        onMarkRead={handleMarkRead}
        onMarkAllRead={handleMarkAllRead}
        isMobile={isMobile}
      />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar — shown on desktop (full or collapsed) and tablet (always collapsed icon-rail).
            Hidden on phones; phone users get the SideDrawer + BottomNav instead. */}
        {shouldShowSidebar && !isPhone && <Sidebar />}

        <main id="main-content" className="main-content" style={{
          flex: 1,
          padding: isPhone ? '1rem 1rem calc(80px + env(safe-area-inset-bottom, 0px)) 1rem'
                  : isTablet ? '1rem 1rem calc(80px + env(safe-area-inset-bottom, 0px)) 1rem'
                  : '2rem 1rem 1rem 1rem',
          overflow: 'auto',
          marginLeft: !isRTL && shouldShowSidebar && !isPhone ? sidebarWidth : 0,
          marginRight: isRTL && shouldShowSidebar && !isPhone ? sidebarWidth : 0,
          transition: 'margin-left 0.3s ease, margin-right 0.3s ease'
        }}>
          <div style={{
            maxWidth: '1440px',
            margin: '0 auto',
            paddingRight: isPhone ? '0' : '1rem',
            paddingLeft: isPhone ? '0' : '1rem'
          }}>
            <PageErrorBoundary key={pathname}>
              <Suspense fallback={<PageLoader />}>
                <PageTransition>{children}</PageTransition>
              </Suspense>
            </PageErrorBoundary>
          </div>
        </main>
      </div>

      {/* Mobile Side Drawer */}
      <SideDrawer
        isOpen={isSideDrawerOpen}
        onClose={() => setIsSideDrawerOpen(false)}
      />

      {/* Bottom Navigation for Mobile */}
      <BottomNav onMenuClick={() => setIsSideDrawerOpen(true)} />

      {/* Tutorial Overlay */}
      <TutorialOverlay />

      {/* Phase 1.8 — offline & sync indicator */}
      <OfflineIndicator />

      {/* Phase 3.1 — global command palette (Cmd/Ctrl+K) */}
      <CommandPalette />

      {/* Phase 2.5 — mobile/tablet floating quick-actions menu */}
      <FabRadial />
    </div>
  );
}

// Platform-appropriate router: BrowserRouter on web/PWA (clean URLs, SPA
// fallback via Firebase Hosting + the service worker); HashRouter on native,
// where Capacitor's embedded static server has no deep-path fallback and a hard
// reload to /students/123 would 404.
const Router = isNative ? HashRouter : BrowserRouter;

/**
 * RouterBridge — mounted inside the Router so it can (a) register the live
 * `navigate` fn for non-component callers (api.js 401, authStore switch-center,
 * ErrorBoundary) and (b) run the native bootstrap once after mount.
 */
function RouterBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigator(navigate);
    return () => clearNavigator();
  }, [navigate]);
  useEffect(() => {
    if (isNative) initNative();
  }, []);
  return null;
}

function AppContent() {
  const { toasts, removeToast } = useToastContext();
  const { user, token, centerType } = useAuthStore();

  return (
    <>
      <Router>
        <RouterBridge />
        <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Admin-issued password reset link. Unauthenticated by design — the
            recipient can't sign in, which is the whole reason they were sent
            one. The single-use token in the query string is the credential. */}
        <Route
          path="/reset-password"
          element={
            <Suspense fallback={<PageLoader />}>
              <ResetPasswordPage />
            </Suspense>
          }
        />

        {/* Claude connector consent (OAuth). Handles its own login redirect so
            it can bring the manager back here after sign-in. */}
        <Route
          path="/connect-claude"
          element={
            <Suspense fallback={<PageLoader />}>
              <ConnectClaudePage />
            </Suspense>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requiresAdmin>
              <AppLayout>
                <DashboardRouter />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/orders"
          element={
            <ProtectedRoute allowedCenterTypes={['pharma_clinic']} requiresAdmin>
              <AppLayout>
                <OrdersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute requiresAdmin>
              <AppLayout>
                <ReportsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/students"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StudentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/teachers"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <TeachersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Payments the center paid its coaches / teachers (Habib 30/10). */}
        <Route
          path="/teachers/payments"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <TeacherPayoutsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <GroupsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/:groupId"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <GroupDetailsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/:groupId/performance"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <GroupPerformanceReportPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/jibaya"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <JibayaPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <AnalyticsDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/therapy-analytics"
          element={
            <ProtectedRoute allowedCenterTypes={['therapy']} requiresAdmin>
              <AppLayout>
                <TherapyAnalyticsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/*
          Canonical hair-transplant CRM lives at /patients now. The old
          /transplant-crm path redirects (preserving query params so
          deep-links like ?view=focus&clientId=X keep working). The three
          deprecated pages — followup-plans, post-op-tracking,
          prp-prospects — also redirect here with a lifecycle filter
          pre-applied via ?lifecycle=<key>, so existing bookmarks land on
          the right view.
        */}
        <Route
          path="/patients"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <TransplantCrmPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/transplant-crm"
          element={<RedirectWithSearch to="/patients" />}
        />

        <Route
          path="/transplant-images"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <TransplantQuickImagesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/*
          Follow-ups (מעקבים): per-treatment-type outreach lists (plasma /
          laser women / laser men), moved off the main dashboard so the big
          screen stays on the pipeline.
        */}
        <Route
          path="/followups"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <FollowUpsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/treatments"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <TreatmentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/followup-plans"
          element={<RedirectWithSearch to="/patients" extraParams={{ lifecycle: 'recent_post_op' }} />}
        />

        <Route
          path="/clinic-documents"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <ClinicDocumentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/prp-prospects"
          element={<RedirectWithSearch to="/patients" extraParams={{ lifecycle: 'prp_eligible' }} />}
        />

        <Route
          path="/clinic-analytics"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <ClinicDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <InventoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/automation"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <AutomationPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/automation/meta-leads"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant', 'learning']} requiresAdmin>
              <AppLayout>
                <MetaLeadsConnectPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/automation/leads"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant', 'learning']} requiresAdmin>
              <AppLayout>
                <MetaLeadsListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Cross-center leads. Super-admin only, and deliberately NOT
            restricted by centerType — it spans every center. */}
        <Route
          path="/automation/all-leads"
          element={
            <ProtectedRoute requiresSuperAdmin>
              <AppLayout>
                <AllCentersLeadsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/automation/meta-leads/status"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant', 'learning']} requiresAdmin>
              <AppLayout>
                <MetaLeadsStatusPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/post-op-tracking"
          element={<RedirectWithSearch to="/patients" extraParams={{ lifecycle: 'recent_post_op' }} />}
        />

        <Route
          path="/workers"
          element={
            <ProtectedRoute allowedCenterTypes={['hair_transplant']} requiresAdmin>
              <AppLayout>
                <WorkersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/surveys"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SurveysPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <OnboardingSubmissionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Course Registration — learning centers only. List of registration
            courses + create; detail page holds the share link, edit/duplicate,
            and the verify/convert-to-group registrants queue. */}
        <Route
          path="/registration"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <RegistrationCoursesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Answers-only view of a campaign. Where the dashboard card lands:
            "how is it doing", not "what is in it and how do I share it". */}
        <Route
          path="/registration/group/:groupId/analytics"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <RegistrationGroupAnalyticsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Link-group drill-in — declared before the :courseId route; React
            Router ranks the static "group" segment above the dynamic param. */}
        <Route
          path="/registration/group/:groupId"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <RegistrationGroupDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/registration/:courseId"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <RegistrationCourseDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Sport sub-type: coach pay per month. The page itself redirects
            when learningSubType !== 'sport'. */}
        <Route
          path="/sport/payroll"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <SportPayrollPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Teaching centers: each teacher's hours per month, for salaries
            (شروق 04/11). Same data as /sport/payroll; page redirects non-teaching centers. */}
        <Route
          path="/teachers/hours"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <TeacherHoursPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Sport sub-type: client balances (replaces the paper الدفعات list). */}
        <Route
          path="/sport/balances"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <SportBalancesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* The payments board (الدفعات) is the balances page. */}
        <Route path="/sport/payments" element={<Navigate to="/sport/balances" replace />} />
        {/* Regular learning centers: the same payments board (kind 'learning'). */}
        <Route
          path="/payments"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <SportBalancesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Caffe sub-type — Tournaments. Learning-gated at the route; the pages
            themselves redirect when learningSubType !== 'caffe'. */}
        <Route
          path="/tournaments"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <TournamentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/:id"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <TournamentDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/teacher-surveys"
          element={
            <ProtectedRoute allowedCenterTypes={['learning']}>
              <AppLayout>
                <TeacherSurveysPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lessons"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <LessonsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lessons/:lessonId/attendance"
          element={
            <ProtectedRoute allowedInTherapyCenter={false} requiresAdmin>
              <AppLayout>
                <LessonAttendancePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route path="/attendance" element={<Navigate to="/lessons" replace />} />
        <Route path="/attendance/:lessonId" element={<AttendanceLessonRedirect />} />

        {/* Worker Attendance (admin) — Live / Timesheets / Reports / Settings.
            Admin-gated and restricted to therapy + hair-transplant centers. */}
        <Route
          path="/staff-attendance"
          element={
            <ProtectedRoute requiresAdmin allowedCenterTypes={['therapy', 'hair_transplant']}>
              <AppLayout>
                <AttendancePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CalendarPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/timetable"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TimetablePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/birthday-gifts"
          element={
            <ProtectedRoute requiresAdmin>
              <AppLayout>
                <BirthdayGiftsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Birthday greetings (sport nav; works for any center type). */}
        <Route
          path="/birthdays"
          element={
            <ProtectedRoute requiresAdmin>
              <AppLayout>
                <BirthdaysPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/teacher/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TeacherDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/worker/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <WorkerDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Route hidden - checklists are included in groups */}
        {/* <Route
          path="/teacher/checklists"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TeacherChecklistsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        /> */}

        <Route
          path="/teacher/attendance"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TeacherAttendancePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* New per-center detail page — single router, two URL aliases.    */}
        {/* `/students/:id` is used by learning centers; `/clients/:id` by   */}
        {/* therapy and hair-transplant centers. Both mount the same         */}
        {/* StudentDetailRouter which picks the right page by centerType.    */}
        <Route
          path="/students/:studentId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StudentDetailRouter />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clients/:studentId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StudentDetailRouter />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy URL redirects (back-compat for bookmarks and internal     */}
        {/* links not yet swept to <StudentLink>). Both old paths land on    */}
        {/* the alias that matches the current center type.                  */}
        <Route path="/student/:studentId"          element={<LegacyRedirect />} />
        <Route path="/student/:studentId/details"  element={<LegacyRedirect />} />

        <Route
          path="/student/:studentId/survey/:surveyId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SurveyResponsePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/survey/:surveyId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SurveyResponsePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/manager/teachers/:teacherId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TeacherEditPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/whatsapp-templates"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PlanGate feature="whatsapp"><WhatsAppTemplatesPage /></PlanGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/whatsapp/inbox"
          element={
            <ProtectedRoute requiresAdmin>
              <AppLayout>
                <PlanGate feature="whatsapp"><WhatsAppInboxPage /></PlanGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/whatsapp/bulk-send"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PlanGate feature="whatsapp"><WhatsAppBulkSendPage /></PlanGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/whatsapp/bulk-send/runs/:runId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PlanGate feature="whatsapp"><WhatsAppBulkRunDetailPage /></PlanGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/audit-log"
          element={
            <ProtectedRoute requiresAdmin>
              <AppLayout>
                <AuditLogPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AccountPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Public Survey Page - No auth required, no layout */}
        <Route
          path="/public/survey/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicSurveyPage />
            </Suspense>
          }
        />

        {/* Public leads share — 24h token-gated read-only leads list.
            No auth, no layout; opened by people without accounts. */}
        <Route
          path="/public/leads/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicLeadsSharePage />
            </Suspense>
          }
        />

        {/* Public link-group menu - No auth. One shared campaign link that
            lists several registration courses (e.g. the same course per
            grade); each row opens that course's own /register/:token form. */}
        <Route
          path="/register/g/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicRegisterGroupPage />
            </Suspense>
          }
        />

        {/* Public Course Registration Page - No auth, reusable per-course
            token in the URL. Parents/students self-register; each submission
            lands as an 'unverified' lead and notifies both parties by WhatsApp. */}
        <Route
          path="/register/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicRegisterPage />
            </Suspense>
          }
        />

        {/* Public Lesson Page - No auth, capability-token authorized.
            Serves both attendance and content views; the API decides which
            view to render based on the token's purpose claim. */}
        <Route
          path="/public/lesson/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicLessonPage />
            </Suspense>
          }
        />

        {/* A teacher's private group-roster link (routes/publicGroupRoster.js). */}
        <Route
          path="/g/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicGroupRosterPage />
            </Suspense>
          }
        />

        {/* Public Timetable Page - No auth. The URL token alone shows nothing:
            the recipient enters their phone and a WhatsApp code, and then only
            ever sees the slice the admin allowlisted their number for. */}
        <Route
          path="/public/timetable/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicTimetablePage />
            </Suspense>
          }
        />

        {/* Public Meeting Page - No auth, capability-token authorized.
            Therapist-facing page for writing session notes and uploading
            files after a meeting; delivered to the therapist by WhatsApp
            5 min before the session starts, valid 24h. */}
        <Route
          path="/public/meeting/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicMeetingPage />
            </Suspense>
          }
        />

        {/* Public Teacher-Onboarding Page - No auth, capability-token
            authorized (48h). New Quran centers' teachers fill in their
            groups + students; submissions land in an admin review queue. */}
        <Route
          path="/public/onboarding/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicOnboardingPage />
            </Suspense>
          }
        />

        {/* Public Client-Intake Page (sport sub-type) - No auth, capability
            token (7 days, single use). The client fills in their details and
            signs; a client record is created with the signed PDF attached. */}
        <Route
          path="/intake/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicIntakePage />
            </Suspense>
          }
        />

        {/* Public staff-contract signing - No auth, capability token (14 days,
            single use). The coach reads the contract and signs; a signed PDF
            lands in their private files. */}
        <Route
          path="/sign-contract/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicSignContractPage />
            </Suspense>
          }
        />

        {/* Public attendance confirmation (sport sub-type) - No auth, capability
            token sent on WhatsApp before a session; closes before it starts. */}
        <Route
          path="/confirm/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicSportConfirmPage />
            </Suspense>
          }
        />

        {/* Public week confirmation (sport sub-type) - No auth; the button of
            the sport_week_schedule WhatsApp: yes / no per session of the week. */}
        <Route
          path="/confirm-week/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicSportWeekConfirmPage />
            </Suspense>
          }
        />

        {/* Help Page - No auth required, no layout */}
        <Route
          path="/help"
          element={
            <Suspense fallback={<PageLoader />}>
              <HelpPage />
            </Suspense>
          }
        />

        {/* Public marketing website - No auth, no layout. centerhubapp.com/website.
            Omitted from native builds: shipping marketing pages inside the app
            binary makes no sense and risks app-store review flags. */}
        {!isNative && (
          <>
            <Route
              path="/website"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WebsiteHomePage />
                </Suspense>
              }
            />
            <Route
              path="/website/pricing"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WebsitePricingPage />
                </Suspense>
              }
            />
            <Route
              path="/website/contact"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WebsiteContactPage />
                </Suspense>
              }
            />
            {/* Connector docs, privacy policy, terms and security page — public
                URLs required by the Claude and ChatGPT directory listings. */}
            <Route
              path="/website/claude"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WebsiteClaudePage />
                </Suspense>
              }
            />
            <Route
              path="/website/privacy"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WebsitePrivacyPage />
                </Suspense>
              }
            />
            <Route
              path="/website/security"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WebsiteSecurityPage />
                </Suspense>
              }
            />
            <Route
              path="/website/terms"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WebsiteTermsPage />
                </Suspense>
              }
            />
          </>
        )}

        <Route
          path="/"
          element={
            !token
              ? <Navigate to="/login" replace />
              : user?.role === 'teacher'
                ? (centerType === 'hair_transplant' ? <Navigate to="/worker/dashboard" replace /> : <Navigate to="/teacher/dashboard" replace />)
                : <Navigate to="/dashboard" replace />
          }
        />

        {/* Catch-all: recover from typos, stale deep links, and unmapped native
            deep links (the SW shell-serves index.html for any path on web).
            Routes to the auth-aware root, which redirects to login/dashboard. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Router>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <PwaPrompts />
    </>
  );
}

export default function App() {
  useEffect(() => {
    // Set HTML direction + lang from the active language (ar/he → rtl).
    applyDocumentDirection(i18n.language);

    // Restore saved theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      document.documentElement.setAttribute('data-theme', savedTheme);
    }
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <YearProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AppContent />
          </ConfirmProvider>
        </ToastProvider>
      </YearProvider>
    </I18nextProvider>
  );
}
